import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useParams } from 'react-router';
import {
  AppShell,
  EmptyState,
  NavItemContent,
  NavSection,
  navItemClass,
  navItemCurrent,
  PageFrame,
  Sidebar,
  SidebarLanguageToggle,
  SidebarThemeToggle,
  SystemBrand,
  Topbar,
  UserProfile,
  useSidebarTheme,
  type IconName,
} from '@hp/ui';
import logoUrl from '@hp/ui/assets/logo.svg';
import logoWhiteUrl from '@hp/ui/assets/logo-white.svg';
import type { Permission } from '@hp/domain';
import { useLogout, useMe } from './api/me';
import { otherLanguage, setLanguage } from './i18n';
import type { resources } from './i18n/resources';
import { BrandMark } from './components/brand-mark';
import Landing from './routes/landing';
import DedupOverview from './routes/dedup-overview';
import Enrichment from './routes/enrichment';
import ContactUpdates from './routes/contact-updates';
import ReviewList from './routes/review-list';
import ReviewDetail from './routes/review-detail';
import Jobs from './routes/jobs';
import JobDetail from './routes/job-detail';
import Audit from './routes/audit';
import AdminUsers from './routes/admin-users';
import Projects from './routes/projects';
import ProjectDetail from './routes/project-detail';
import Segmentation from './routes/segmentation';
import Schedules from './routes/schedules';
import Performance from './routes/performance';
import PerformanceData from './routes/performance-data';
import PerformanceMappings from './routes/performance-mappings';
import Leads from './routes/leads';

// --- Navigation model: sections with items, or standalone section links (data-driven, one entry per screen) ---

/** Labels are keys of the `shell` catalog's `nav` block (`t('nav.<key>')`). */
type NavKey = keyof (typeof resources)['en']['shell']['nav'];

interface NavItem {
  label: NavKey;
  to: string;
  icon: IconName;
  /** Match this route exactly (don't stay active on deeper paths). */
  end?: boolean;
}

interface NavSectionModel {
  id: string;
  label: NavKey;
  items: NavItem[];
}

const NAV_SECTIONS: NavSectionModel[] = [
  {
    id: 'crm',
    label: 'crm',
    items: [
      { label: 'overview', to: '/crm-data', icon: 'grid', end: true },
      { label: 'enrichment', to: '/crm-data/enrichment', icon: 'badgeCheck' },
      { label: 'contactUpdates', to: '/crm-data/contact-updates', icon: 'mail' },
      { label: 'deduplication', to: '/crm-data/deduplication', icon: 'users' },
      { label: 'mergeJobs', to: '/crm-data/jobs', icon: 'merge' },
      { label: 'segmentation', to: '/crm-data/segmentation', icon: 'target' },
    ],
  },
  {
    id: 'marketing',
    label: 'marketing',
    items: [
      { label: 'performance', to: '/marketing/performance', icon: 'chart' },
      { label: 'leads', to: '/marketing/leads', icon: 'team' },
    ],
  },
  {
    id: 'master',
    label: 'master',
    items: [{ label: 'projects', to: '/projects', icon: 'projects' }],
  },
  {
    id: 'system',
    label: 'system',
    items: [
      { label: 'schedules', to: '/schedules', icon: 'clock' },
      { label: 'audit', to: '/audit', icon: 'audit' },
      { label: 'users', to: '/users', icon: 'user' },
    ],
  },
];

/** Pages that need a permission to be useful; everything else is readable by every signed-in user. */
const ROUTE_PERMISSION: Partial<Record<string, Permission>> = { '/users': 'users.manage', '/audit': 'audit.read' };

/** Section + page (+ detail marker) for the topbar breadcrumbs and document.title. */
function describeLocation(pathname: string): {
  section: NavSectionModel | null;
  item: NavItem | null;
  detail: boolean;
} {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`)) {
        return { section, item, detail: pathname !== item.to };
      }
    }
  }
  return { section: null, item: null, detail: false };
}

/** Sidebar section labels are uppercase ("CRM DATA"); breadcrumbs and titles read them as prose, keeping acronyms. */
function titleCase(s: string): string {
  return s
    .split(' ')
    .map((word, i) =>
      word === 'CRM' ? word : i === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase(),
    )
    .join(' ');
}

function AppSidebar({
  can,
  mobileOpen,
  onNavigate,
}: {
  can: (permission: Permission) => boolean;
  mobileOpen: boolean;
  onNavigate: () => void;
}) {
  const { t, i18n } = useTranslation(['shell', 'common']);
  const me = useMe();
  const logout = useLogout();
  const theme = useSidebarTheme();
  const location = useLocation();
  const here = describeLocation(location.pathname);
  const nextLanguage = otherLanguage(i18n.language);
  const nextLanguageName = t(`common:language.${nextLanguage}`);

  return (
    <Sidebar
      dark={theme.dark}
      mobileOpen={mobileOpen}
      ariaLabel={t('sidebar.mainNavigation')}
      brand={
        <SystemBrand
          href="/crm-data"
          logoSrc={logoUrl}
          logoDarkSrc={logoWhiteUrl}
          ariaLabel={t('sidebar.brandAria')}
          onClick={onNavigate}
        />
      }
      bottom={
        <>
          <SidebarThemeToggle
            dark={theme.dark}
            onToggle={theme.toggle}
            labelDark={t('sidebar.darkPanel')}
            labelLight={t('sidebar.darkPanel')}
            ariaLabel={theme.dark ? t('sidebar.switchToLight') : t('sidebar.switchToDark')}
          />
          <SidebarLanguageToggle
            label={nextLanguageName}
            ariaLabel={t('common:language.switchTo', { language: nextLanguageName })}
            onClick={() => void setLanguage(nextLanguage)}
          />
          {me.data && (
            <UserProfile
              name={me.data.displayName ?? me.data.email}
              role={`${t(`common:role.${me.data.role}`)} · ${t('sidebar.signOut')}`}
              trailingIcon="logout"
              ariaLabel={t('sidebar.signOutAria')}
              onClick={() => void logout()}
            />
          )}
        </>
      }
    >
      {NAV_SECTIONS.map((section) => {
        const items = section.items.filter((item) => {
          const needed = ROUTE_PERMISSION[item.to];
          return needed === undefined || can(needed);
        });
        if (items.length === 0) return null;
        const sectionLabel = t(`nav.${section.label}`);
        return (
          <NavSection
            key={section.id}
            id={section.id}
            label={sectionLabel}
            ariaLabel={titleCase(sectionLabel)}
            forceOpen={here.section?.id === section.id}
          >
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={({ isActive }) => {
                  const detail = isActive && !item.end && location.pathname !== item.to;
                  return navItemClass({ active: isActive && !detail, currentParent: detail });
                }}
                aria-current={(() => {
                  const isActive = item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
                  const detail = isActive && location.pathname !== item.to;
                  return navItemCurrent({ active: isActive && !detail, currentParent: detail });
                })()}
              >
                <NavItemContent icon={item.icon}>{t(`nav.${item.label}`)}</NavItemContent>
              </NavLink>
            ))}
          </NavSection>
        );
      })}
    </Sidebar>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['shell', 'common']);
  const me = useMe();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const here = describeLocation(location.pathname);
  const sectionTitle = here.section ? titleCase(t(`nav.${here.section.label}`)) : null;
  const pageTitle = here.item ? t(`nav.${here.item.label}`) : null;
  const appTitle = t('common:app.title');

  useEffect(() => {
    document.title = [pageTitle, sectionTitle, appTitle].filter(Boolean).join(' · ');
  }, [pageTitle, sectionTitle, appTitle]);

  const crumbs: ReactNode[] = [
    <Link key="root" to="/crm-data">
      {t('crumbs.root')}
    </Link>,
  ];
  if (sectionTitle) crumbs.push(<span key="section">{sectionTitle}</span>);
  if (here.item && pageTitle) {
    crumbs.push(
      here.detail ? (
        <Link key="page" to={here.item.to}>
          {pageTitle}
        </Link>
      ) : (
        <strong key="page">{pageTitle}</strong>
      ),
    );
  }
  if (here.detail) crumbs.push(<strong key="detail">{t('crumbs.detail')}</strong>);

  return (
    <AppShell
      skipLabel={t('sidebar.skipToContent')}
      sidebar={
        me.data && (
          <AppSidebar
            can={(permission) => me.data?.permissions.includes(permission) ?? false}
            mobileOpen={mobileOpen}
            onNavigate={() => setMobileOpen(false)}
          />
        )
      }
      topbar={
        <Topbar breadcrumbs={crumbs} menuLabel={t('sidebar.openNavigation')} onMenu={() => setMobileOpen((o) => !o)} />
      }
    >
      <div className="hp-page">{children}</div>
    </AppShell>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <PageFrame>
      <EmptyState
        icon="search"
        title={t('errors.pageNotFound')}
        action={
          <Link to="/crm-data" className="hp-button hp-button--secondary">
            <span>{t('errors.backToOverview')}</span>
          </Link>
        }
      >
        {t('errors.pageNotFoundBody')}
      </EmptyState>
    </PageFrame>
  );
}

/** "/" → "/crm-data", keeping query params (the OIDC callback lands on /?auth_error=…). */
function RootRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/crm-data', search: location.search }} replace />;
}

/** Old bookmarked detail URLs (/review/:id, /jobs/:id) → new section paths. */
function LegacyIdRedirect({ base }: { base: string }) {
  const { id } = useParams();
  return <Navigate to={id ? `${base}/${id}` : base} replace />;
}

export function AppRouter() {
  const me = useMe();

  if (me.isLoading) {
    return (
      <div className="bo-landing hp-page" aria-busy="true">
        <BrandMark className="bo-landing__mark" style={{ margin: 'auto', color: '#fff' }} />
      </div>
    );
  }
  // Logged out: the branded landing IS the app, whatever the path. After
  // sign-in, the OIDC returnTo brings the user back to where they aimed.
  if (!me.data) return <Landing />;

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        {/* CRM data section */}
        <Route path="/crm-data" element={<DedupOverview />} />
        <Route path="/crm-data/enrichment" element={<Enrichment />} />
        <Route path="/crm-data/contact-updates" element={<ContactUpdates />} />
        <Route path="/crm-data/deduplication" element={<ReviewList />} />
        <Route path="/crm-data/deduplication/:id" element={<ReviewDetail />} />
        <Route path="/crm-data/jobs" element={<Jobs />} />
        <Route path="/crm-data/jobs/:id" element={<JobDetail />} />
        <Route path="/crm-data/segmentation" element={<Segmentation />} />
        {/* Other sections */}
        <Route path="/marketing/performance" element={<Performance />} />
        <Route path="/marketing/performance/data" element={<PerformanceData />} />
        <Route path="/marketing/performance/mappings" element={<PerformanceMappings />} />
        <Route path="/marketing/leads" element={<Leads />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/schedules" element={<Schedules />} />
        <Route path="/users" element={<AdminUsers />} />
        <Route path="/audit" element={<Audit />} />
        {/* Legacy redirects (old bookmarks) */}
        <Route path="/crm-data/review" element={<Navigate to="/crm-data/deduplication" replace />} />
        <Route path="/crm-data/review/:id" element={<LegacyIdRedirect base="/crm-data/deduplication" />} />
        <Route path="/dedup" element={<Navigate to="/crm-data" replace />} />
        <Route path="/dedup/review" element={<Navigate to="/crm-data/deduplication" replace />} />
        <Route path="/dedup/review/:id" element={<LegacyIdRedirect base="/crm-data/deduplication" />} />
        <Route path="/dedup/jobs" element={<Navigate to="/crm-data/jobs" replace />} />
        <Route path="/dedup/jobs/:id" element={<LegacyIdRedirect base="/crm-data/jobs" />} />
        <Route path="/dedup/enrichment" element={<Navigate to="/crm-data/enrichment" replace />} />
        <Route path="/review" element={<Navigate to="/crm-data/deduplication" replace />} />
        <Route path="/review/:id" element={<LegacyIdRedirect base="/crm-data/deduplication" />} />
        <Route path="/jobs" element={<Navigate to="/crm-data/jobs" replace />} />
        <Route path="/jobs/:id" element={<LegacyIdRedirect base="/crm-data/jobs" />} />
        <Route path="/admin/users" element={<Navigate to="/users" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}
